import { TitleCasePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppDataService } from '../../core/services/app-data.service';
import { EquipmentService } from '../../core/services/equipment.service';
import { ExportService } from '../../core/services/export.service';
import { MaintenanceService } from '../../core/services/maintenance.service';
import {
  MaintenanceJob,
  MaintenanceJobStatus,
  MaintenanceJobType,
  MaintenancePriority,
} from '../../core/models/maintenance.models';
import { currentTimestamp, formatDate } from '../../core/utils/format.util';
import { confirmAction } from '../../core/utils/confirm.util';
import { matchesItemQuery } from '../../core/utils/item-filter.util';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-maitenance-jobs',
  standalone: true,
  imports: [FormsModule, TitleCasePipe],
  templateUrl: './maitenance-jobs.component.html',
  styleUrl: './maitenance-jobs.component.css',
})
export class MaitenanceJobsComponent implements OnInit {
  private readonly maintenanceService = inject(MaintenanceService);
  private readonly equipmentService = inject(EquipmentService);
  private readonly appData = inject(AppDataService);
  private readonly exporter = inject(ExportService);
  protected readonly auth = inject(AuthService);

  readonly jobStats = this.maintenanceService.jobStats;
  readonly timestamp = currentTimestamp();
  readonly formatDate = formatDate;

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.appData.refreshAll();
    }
  }

  readonly searchQuery = signal('');
  readonly statusFilter = signal('');
  readonly typeFilter = signal('');
  readonly showMoreFilters = signal(false);
  readonly priorityFilter = signal('');
  readonly assignedToFilter = signal('');

  readonly filteredJobs = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const status = this.statusFilter();
    const type = this.typeFilter();
    const priority = this.priorityFilter();
    const assignedTo = this.assignedToFilter().trim().toLowerCase();

    return this.maintenanceService.allJobs().filter((job) => {
      if (status && job.status !== status) {
        return false;
      }
      if (type && job.type !== type) {
        return false;
      }
      if (priority && job.priority !== priority) {
        return false;
      }
      if (assignedTo && !job.assignedTo.toLowerCase().includes(assignedTo)) {
        return false;
      }
      if (
        search &&
        !job.jobId.toLowerCase().includes(search) &&
        !job.assetName.toLowerCase().includes(search) &&
        !job.description.toLowerCase().includes(search)
      ) {
        return false;
      }
      return true;
    });
  });

  readonly assets = this.equipmentService.allAssets;
  readonly assetNameFilter = signal('');
  readonly filteredAssets = computed(() => {
    const query = this.assetNameFilter();
    const selectedId = this.formAssetId();
    return this.assets().filter(
      (asset) =>
        asset.id === selectedId ||
        matchesItemQuery(query, asset.name, asset.assetId),
    );
  });

  readonly showJobModal = signal(false);
  readonly showViewModal = signal(false);
  readonly showUpdateModal = signal(false);
  readonly showCompleteModal = signal(false);

  readonly selectedJob = signal<MaintenanceJob | null>(null);

  readonly formAssetId = signal('');
  readonly formType = signal<MaintenanceJobType>('repair');
  readonly formPriority = signal<MaintenancePriority>('medium');
  readonly formAssignedTo = signal('');
  readonly formSentBy = signal('');
  readonly formStartDate = signal(new Date().toISOString().slice(0, 10));
  readonly formDescription = signal('');
  readonly formStatus = signal<MaintenanceJobStatus>('pending');

  readonly showToast = signal(false);
  readonly toastMessage = signal('');

  openJobModal(): void {
    this.assetNameFilter.set('');
    this.formAssetId.set(this.assets()[0]?.id ?? '');
    this.formType.set('repair');
    this.formPriority.set('medium');
    this.formAssignedTo.set('');
    this.formSentBy.set(this.auth.currentUserName());
    this.formStartDate.set(new Date().toISOString().slice(0, 10));
    this.formDescription.set('');
    this.formStatus.set('pending');
    this.showJobModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeJobModal(): void {
    this.showJobModal.set(false);
    document.body.style.overflow = 'auto';
  }

  openViewModal(job: MaintenanceJob): void {
    this.selectedJob.set(job);
    this.showViewModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeViewModal(): void {
    this.showViewModal.set(false);
    this.selectedJob.set(null);
    document.body.style.overflow = 'auto';
  }

  openUpdateModal(job: MaintenanceJob): void {
    this.selectedJob.set(job);
    this.formType.set(job.type);
    this.formPriority.set(job.priority);
    this.formAssignedTo.set(job.assignedTo);
    this.formStartDate.set(job.startDate);
    this.formDescription.set(job.description);
    this.formStatus.set(job.status);
    this.showUpdateModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeUpdateModal(): void {
    this.showUpdateModal.set(false);
    this.selectedJob.set(null);
    document.body.style.overflow = 'auto';
  }

  openCompleteModal(job: MaintenanceJob): void {
    this.selectedJob.set(job);
    this.showCompleteModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeCompleteModal(): void {
    this.showCompleteModal.set(false);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeJobModal();
      this.closeViewModal();
      this.closeUpdateModal();
      this.closeCompleteModal();
    }
  }

  createJob(): void {
    const asset = this.assets().find((row) => row.id === this.formAssetId());
    const sentBy = this.formSentBy().trim() || this.auth.currentUserName();
    const fixer = this.formAssignedTo().trim();
    if (!asset || !this.formDescription().trim() || !fixer) {
      return;
    }

    this.maintenanceService.addJob({
      assetName: asset.name,
      assetId: asset.id,
      type: this.formType(),
      description: this.formDescription().trim(),
      priority: this.formPriority(),
      assignedTo: fixer,
      sentBy,
      startDate: this.formStartDate(),
      status: this.formStatus(),
      icon: asset.icon,
    });

    this.closeJobModal();
    this.showSuccessToast('Maintenance job created successfully');
  }

  updateJob(): void {
    const job = this.selectedJob();
    if (!job || !this.formDescription().trim()) {
      return;
    }

    const updated = this.maintenanceService.updateJob(job.id, {
      type: this.formType(),
      priority: this.formPriority(),
      assignedTo: this.formAssignedTo(),
      startDate: this.formStartDate(),
      description: this.formDescription().trim(),
      status: this.formStatus(),
    });
    if (!updated) {
      this.showSuccessToast('Job update is not available via the API yet.');
      return;
    }
    this.closeUpdateModal();
    this.showSuccessToast('Job updated successfully');
  }

  deleteJob(job: MaintenanceJob): void {
    if (!this.auth.hasPermission('delete_orders')) {
      return;
    }
    if (!confirmAction(`Delete job "${job.jobId}"? This cannot be undone.`)) {
      return;
    }

    if (!this.maintenanceService.deleteJob(job.id)) {
      this.showSuccessToast('Job delete is not available via the API yet.');
      return;
    }

    this.showSuccessToast('Job deleted successfully');
  }

  completeJob(): void {
    const job = this.selectedJob();
    if (!job) {
      return;
    }

    this.maintenanceService
      .completeJobViaApi(job.jobId || job.id)
      .subscribe((result) => {
        if (result.success) {
          this.closeCompleteModal();
          this.appData.refreshAll();
          this.showSuccessToast(
            result.message ??
              'Job completed. Item removed from Damaged / Lost and returned to stock.',
          );
        } else {
          this.showSuccessToast(result.message ?? 'Failed to complete job');
        }
      });
  }

  showSuccessToast(message: string): void {
    this.toastMessage.set(message);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  closeToast(): void {
    this.showToast.set(false);
  }

  exportData(): void {
    this.exporter.downloadCsv(
      `maintenance-jobs-${new Date().toISOString().slice(0, 10)}`,
      this.filteredJobs().map((job) => ({ ...job })),
    );
  }

  toggleMoreFilters(): void {
    this.showMoreFilters.update((open) => !open);
  }

  clearMoreFilters(): void {
    this.priorityFilter.set('');
    this.assignedToFilter.set('');
  }
}
